import { isDefined } from "@clipboard-health/util-ts";
import { Chip, Skeleton, Stack } from "@mui/material";
import { isPast } from "date-fns";

import { useSession } from "../Auth/api/useSession";
import { APP_V2_APP_EVENTS, logError } from "../lib/analytics";
import { SHIFT_TIME_PROPOSAL_STATUS } from "../ShiftTimeProposals/api/schema";
import { useGetShiftTimeProposalById } from "../ShiftTimeProposals/api/useShiftTimeProposals";
import { PACIFIC_TIME_ZONE } from "../Timezone/constants";
import { ChatWidgetCard } from "./ChatWidgetCard";
import {
  EXPIRED_SHIFT_TIME_PROPOSAL_CHIP,
  formatShiftStartEndDateTime,
  SHIFT_TIME_PROPOSAL_STATUS_CHIP_MAP,
} from "./utils";

interface ShiftTimeProposalRequestMessageProps {
  shiftTimeProposalId: string;
  originalStartTime: string;
  originalEndTime: string;
}

export function ShiftTimeProposalRequestMessage(props: ShiftTimeProposalRequestMessageProps) {
  const { shiftTimeProposalId, originalStartTime, originalEndTime } = props;

  const { profile } = useSession();
  const {
    data: shiftTimeProposalResponse,
    isLoading,
    isError,
    error,
  } = useGetShiftTimeProposalById({ shiftTimeProposalId });

  const shiftTimeProposal = shiftTimeProposalResponse?.data;

  if (isLoading) {
    return (
      <Stack alignItems="flex-end" className="sendbird-admin-message">
        <Skeleton variant="rounded" width={400} height={100} />
      </Stack>
    );
  }

  if (!isDefined(shiftTimeProposal) || isError) {
    logError(APP_V2_APP_EVENTS.SHIFT_TIME_PROPOSAL_MESSAGE_RENDER_FAILURE, {
      error: isError ? error : new Error("Shift time proposal data is undefined"),
      metadata: { shiftTimeProposalId },
    });
    return null;
  }

  const { status, proposedStartTime, proposedEndTime } = shiftTimeProposal.attributes;
  const timeZone = profile?.tmz ?? PACIFIC_TIME_ZONE;

  const proposedShiftStartEnd = formatShiftStartEndDateTime(
    proposedStartTime,
    proposedEndTime,
    timeZone
  );

  const originalShiftStartEnd = formatShiftStartEndDateTime(
    originalStartTime,
    originalEndTime,
    timeZone
  );

  const isExpired =
    status === SHIFT_TIME_PROPOSAL_STATUS.PROPOSED && isPast(new Date(proposedStartTime));

  const chipProps = isExpired
    ? EXPIRED_SHIFT_TIME_PROPOSAL_CHIP
    : SHIFT_TIME_PROPOSAL_STATUS_CHIP_MAP[status];

  return (
    <Stack alignItems="flex-end" className="sendbird-admin-message">
      <ChatWidgetCard
        statusChip={
          <Chip
            label={chipProps.label}
            aria-label={chipProps.label}
            color={chipProps.color}
            variant="outlined"
            sx={{
              border: "none",
              borderRadius: 2,
              fontWeight: "bold",
              ...chipProps.customStyles,
            }}
          />
        }
        title="You proposed a new shift time"
        subtitle={proposedShiftStartEnd}
        caption={`Previous: ${originalShiftStartEnd}`}
      />
    </Stack>
  );
}
